import React, { useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useMatch } from 'react-router-dom'
import { usePayment } from 'contexts/payment'
import { useAccess } from 'contexts/access'
import { useUpgradable } from 'hooks/upgradable'
import { useDocuments } from 'contexts/documents'
import { useWithinQuota } from 'hooks/within-quota'
import paths from 'app/paths'
import { cls } from 'utilities/dom'
import FunctionalityNotice from 'components/functionality-notice'
import { HeroGrid, ActionColumn } from './s'
import ModuleTitle from 'components/module-title'
import SigningPanel from 'pages/documents/panels/signing'
import LimitStatus from './limit-status'
import ContextMenu from 'widgets/context-menu'
import Modal from 'modals/dialog'
import DocumentSigningQuota from 'pages/documents/modals/signing-quota'
import ExtendAccess from './extend-access'
import { SmartDocument } from 'components/feather'

const DocumentsHero = ({ salt, ...props }) => {
    const { formatMessage } = useIntl()

    const baseMatch = useMatch({
        path: paths.documents.base,
        exact: true
    })

    const {
        checkFeature,
        check
    } = useAccess()

    const checkUpgradable = useUpgradable()

    const {
        selection,
        numberOfDocumentsOnlyOwnedByMe,

        getNumberOfDocumentsOnlyOwnedByMe
    } = useDocuments()

    const signingFeature = checkFeature('digital-signing')
    const signingUpgradable = checkUpgradable({ feature: 'digital-signing' })
    const signingAccess = check('digital-signing:manage')// || check('unit:document-signing:manage')

    const billingAccess = check('organization:manage')

    const [, quota = {}] = useWithinQuota({
        prefix: 'feature',
        key: 'digital-signing',
        dependencies: [signingFeature, signingAccess || billingAccess]
    })

    const [reviewingSigningQuota, setReviewingSigningQuota] = useState(false)
    const [bulkExtendingAccess, setBulkExtendingAccess] = useState(false)

    useEffect(() => void getNumberOfDocumentsOnlyOwnedByMe(), [])

    const actions = !!numberOfDocumentsOnlyOwnedByMe ? [
        () => ({
            salt: `${salt}:access:extend`,
            label: formatMessage({
                id: 'action_extend_access',
                defaultMessage: 'Extend access'
            }),
            effect: 'neutral',
            onClick: () => setBulkExtendingAccess(true)
        })
    ] : null

    const selected = !!selection.length

    const className = cls([
        selected && 'has-selected',
        props?.className
    ])

    return (
        <>
            <FunctionalityNotice
                access={true}
                icon={SmartDocument}
                storageKey="smart-documents"
                heading={formatMessage({
                    id: 'banner_smart_documents_heading',
                    defaultMessage: 'Smart documents and templates 💥<misty>Build employment contracts and other HR documents quickly</misty>'
                }, { misty: chunks => <span>{chunks}</span> })}
                caption={formatMessage({
                    id: 'banner_smart_documents_caption',
                    defaultMessage: 'Add your own document templates and create documents directly in Huma. Smart fields will automatically fill in data from employees, teams, locations or the organization. When you’re done, the document can be sent for digital signing right away.'
                })}
                knowledgebaseSlug="smart-documents" />
            <HeroGrid {...(className ? { className } : null)}>
                <ModuleTitle
                    which="documents"
                    className="title" />
                {!!baseMatch && (
                    <>
                        {(!!signingFeature && !signingUpgradable) && <SigningPanel salt={salt} />}
                        {!!numberOfDocumentsOnlyOwnedByMe && (
                            <ActionColumn>
                                <ContextMenu
                                    salt={salt}
                                    actions={actions} />
                            </ActionColumn>
                        )}
                    </>
                )}
            </HeroGrid>
            {(!!baseMatch && !!signingFeature && !signingUpgradable) && (
                <LimitStatus
                    quota={quota}
                    viewDetails={() => setReviewingSigningQuota(true)} />
            )}
            {(!!baseMatch && !!signingAccess) && (
                <Modal
                    show={reviewingSigningQuota}
                    dismiss={() => setReviewingSigningQuota(false)}
                    className="scrollable"
                    salt={`${salt}:review-signing-quota`}>
                    <DocumentSigningQuota
                        dismiss={() => setReviewingSigningQuota(false)}
                        salt={`${salt}:review-signing-quota`} />
                </Modal>
            )}
            {(!!baseMatch && !!numberOfDocumentsOnlyOwnedByMe) && (
                <Modal
                    show={!!bulkExtendingAccess}
                    dismiss={() => setBulkExtendingAccess(false)}
                    className="scrollable"
                    salt={`${salt}:access:extension`}>
                    <ExtendAccess
                        count={numberOfDocumentsOnlyOwnedByMe}
                        salt={`${salt}:access:extension`} />
                </Modal>
            )}
        </>
    )
}

export default DocumentsHero